import React from 'react';
import { Button, Form } from 'react-bootstrap';
import { emptyRow, varTerm, constTerm } from '../../util/areaFormulaBuilder';
import { AREA_VAR_LABELS } from '../../util/marineCalc';
import { formulaText } from '../../util/areaFormulaDisplay';

/**
 * Rows of terms. Each row is a product; rows are summed. Controlled: every
 * edit calls `onChange` with a fresh rows array and nothing is kept here.
 *
 * `allowedVars` is the set the user ticked above the builder — the variable
 * dropdown offers only those, so a row can never reference a dimension the
 * estimator will not be asked for. A LEGACY term (divide_by, or a var outside
 * the palette) renders read-only: it can be removed, never edited.
 */
export default function FormulaBuilder({ rows, allowedVars, onChange }) {
  const list = rows || [];
  const vars = allowedVars || [];

  const update = (next) => onChange(next);
  const setTerm = (ri, ti, term) =>
    update(list.map((r, i) => (i !== ri ? r : { terms: r.terms.map((t, j) => (j === ti ? term : t)) })));
  const addTerm = (ri, term) =>
    update(list.map((r, i) => (i !== ri ? r : { terms: [...r.terms, term] })));
  const removeTerm = (ri, ti) =>
    update(list.map((r, i) => (i !== ri ? r : { terms: r.terms.filter((_, j) => j !== ti) })));
  const addRow = () => update([...list, vars.length ? { terms: [varTerm(vars[0])] } : emptyRow()]);
  const removeRow = (ri) => update(list.filter((_, i) => i !== ri));

  return (
    <div className="border rounded p-2 bg-light">
      {list.length === 0 && (
        <div className="text-muted small mb-2">No rows yet. Add one to start the formula.</div>
      )}
      {list.map((row, ri) => (
        <div key={ri} className="d-flex align-items-center flex-wrap mb-2">
          <span className="text-muted small mr-2" style={{ minWidth: '3.5rem' }}>
            {ri === 0 ? 'Row 1' : `+ Row ${ri + 1}`}
          </span>
          {row.terms.map((t, ti) => (
            <React.Fragment key={ti}>
              {ti > 0 && <span className="mx-1">×</span>}
              {t.kind === 'var' && (
                <Form.Control
                  as="select"
                  size="sm"
                  style={{ width: 'auto' }}
                  value={t.name}
                  onChange={(e) => setTerm(ri, ti, varTerm(e.target.value))}
                >
                  {vars.map((v) => (
                    <option key={v} value={v}>{AREA_VAR_LABELS[v] || v}</option>
                  ))}
                </Form.Control>
              )}
              {t.kind === 'const' && (
                <Form.Control
                  type="number"
                  size="sm"
                  step="0.01"
                  min="0"
                  style={{ width: '6rem' }}
                  value={t.value}
                  placeholder="1.00"
                  onChange={(e) => setTerm(ri, ti, constTerm(e.target.value))}
                />
              )}
              {t.kind === 'legacy' && (
                <span
                  className="text-monospace small px-2 py-1 border rounded bg-white"
                  title="Set by an earlier version. It can be removed but not edited."
                >
                  {formulaText({ sum: [{ product: [t.term] }] }, AREA_VAR_LABELS) || '?'}
                </span>
              )}
              <Button
                variant="link"
                size="sm"
                className="text-danger px-1"
                title="Remove term"
                onClick={() => removeTerm(ri, ti)}
              >
                <i className="fa-sharp fa-regular fa-xmark" />
              </Button>
            </React.Fragment>
          ))}
          <span className="ml-auto d-flex align-items-center">
            <Form.Control
              as="select"
              size="sm"
              style={{ width: 'auto' }}
              value=""
              onChange={(e) => {
                const v = e.target.value;
                if (v === '__const') addTerm(ri, constTerm('1'));
                else if (v) addTerm(ri, varTerm(v));
              }}
            >
              <option value="">+ term…</option>
              {vars.map((v) => (
                <option key={v} value={v}>× {AREA_VAR_LABELS[v] || v}</option>
              ))}
              <option value="__const">× number</option>
            </Form.Control>
            <Button
              variant="link"
              size="sm"
              className="text-muted px-1 ml-1"
              title="Remove row"
              onClick={() => removeRow(ri)}
            >
              <i className="fa-sharp fa-regular fa-trash" />
            </Button>
          </span>
        </div>
      ))}
      <Button variant="outline-secondary" size="sm" onClick={addRow} disabled={vars.length === 0}>
        <i className="fa-sharp fa-regular fa-plus mr-1" /> Add row
      </Button>
    </div>
  );
}
